import { apiClient } from './api-client';
import type { Response } from './api/data-contracts';
import { hasLocation, transformResponse, type LocatedStation } from './fuel-station-api';

/**
 * 전국 주유소 스냅샷 — IndexedDB 캐시 + ETag 조건부 GET.
 *
 * 저장본이 있으면 즉시 콜백하고(0ms 렌더) 백그라운드로 If-None-Match 재검증한다.
 * 304면 그대로, 200이면 갱신본으로 다시 콜백한다 (stale-while-revalidate).
 * raw ~4.6MB 라 localStorage(Safari 5MB) 대신 IndexedDB 를 쓴다 — 구조화 객체
 * 그대로 저장되어 stringify/parse 도 없다. 저장 실패는 무시하고 네트워크로 동작한다.
 */

const DB_NAME = 'wjpnc-map';
const STORE = 'snapshot';
const KEY = 'nationwide';

export interface StationSnapshot {
  stations: LocatedStation[];
  etag: string;
  /** 저장 시각(ISO) — "가격 기준 시각" 표시·수동 새로고침 판단용 */
  savedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readStored(): Promise<StationSnapshot | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const req = db.transaction(STORE).objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function writeStored(snapshot: StationSnapshot): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(snapshot, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // 쿼터 초과·시크릿 모드 등 — 캐시는 최적화일 뿐이므로 무시
  }
}

async function fetchSnapshot(etag?: string): Promise<StationSnapshot | 'not-modified'> {
  const response = await apiClient.get('fuel-stations/search/snapshot', {
    headers: etag ? { 'If-None-Match': etag } : undefined,
    throwHttpErrors: false,
    timeout: 60000, // 전국 ~1.1만 건 (gzip ~630KB)
  });
  if (response.status === 304) return 'not-modified';
  if (!response.ok) throw new Error(`스냅샷 조회 실패: ${response.status}`);
  const rows = await response.json<Response[]>();
  return {
    stations: rows.map(transformResponse).filter(hasLocation),
    etag: response.headers.get('ETag') ?? '',
    savedAt: new Date().toISOString(),
  };
}

/**
 * 스냅샷을 로드한다. 저장본이 있으면 즉시 1회, 서버에 갱신분이 있으면 갱신 후 1회 더
 * onData 를 호출한다. 반환된 함수를 호출하면 이후 콜백이 중단된다.
 */
export function loadStationSnapshot(
  onData: (snapshot: StationSnapshot) => void,
  onError?: (err: unknown) => void
): () => void {
  let cancelled = false;
  (async () => {
    const stored = await readStored();
    if (cancelled) return;
    if (stored) onData(stored);
    try {
      const fetched = await fetchSnapshot(stored?.etag);
      if (cancelled || fetched === 'not-modified') return;
      await writeStored(fetched);
      if (!cancelled) onData(fetched);
    } catch (err) {
      // 저장본으로 이미 렌더된 상태면 조용히 유지, 최초 로드 실패만 알린다
      if (!stored && !cancelled) onError?.(err);
    }
  })();
  return () => { cancelled = true; };
}
